/**
 * Loyalty — a bonus balance in lari on the customer (migration 085).
 *
 * THE SHAPE. A sale with a customer EARNS `loyaltyPct` % of what the customer
 * paid; the balance can be SPENT on a later sale as `bonus_redeemed`, a third
 * term between the lines and the total:
 *
 *     total = lines − discount − bonus
 *
 * It sits next to the cart discount but is NOT one: it is the customer's own
 * money coming back, so it does not count against a cashier's ceiling (084),
 * and it earns nothing itself (earn is a percent of `total`, after the bonus
 * came off). The server is the authority for all of it — it reads the percent
 * from settings, checks the balance row-locked, writes the ledger. These
 * helpers exist so the till shows the SAME numbers the server will book.
 */

import { roundMoney, toTetri } from './money-round'

/**
 * What a sale of `paid` lari earns at `pct` %. Mirrors create_sale:
 * `round(p_total * pct / 100, 2)`; 0 when there is nothing to earn on.
 */
export function earnOn(paid: number, pct: number): number {
  if (!(paid > 0) || !(pct > 0)) return 0
  // 1.53 — rounded on the decimal digits, as round(…, 2) in SQL (lib/money-round)
  return roundMoney(paid * Math.min(pct, 100) / 100)
}

/**
 * How much of a balance a cart can take: the whole balance, capped by what
 * is payable after the cart discount. Never negative — a customer whose
 * balance went below zero (a return took back bonus already spent) has
 * nothing to redeem.
 */
export function redeemable(balance: number, payable: number): number {
  if (!(balance > 0) || !(payable > 0)) return 0
  // 1.53 — capped by the payable as the database will store it, in whole tetri
  return Math.min(toTetri(balance), toTetri(payable)) / 100
}

/**
 * The percent as the settings row stores it. The server accepts
 * `^[0-9]+(\.[0-9]+)?$` and caps at 100; anything else counts as 0. Same rule
 * here so the settings card refuses what the server would silently zero.
 */
export function parseLoyaltyPct(value: string): number | null {
  const s = value.trim()
  if (!/^[0-9]+(\.[0-9]+)?$/.test(s)) return null
  const n = Number(s)
  return n > 100 ? null : n
}

/** Labels for the ledger, as the owner reads them. */
export const BONUS_KIND_LABELS: Record<string, string> = {
  earn:          'დაერიცხა',
  redeem:        'გამოიყენა',
  return_earn:   'დაბრუნება — ჩამოეჭრა',
  return_redeem: 'დაბრუნება — დაუბრუნდა',
  adjust:        'კორექცია',
}
